import { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import JobCard from "@/components/JobCard";
import JobDialog from "@/components/JobDialog";
import JobWeather from "@/components/JobWeather";
import JobsCalendar from "@/components/JobsCalendar";
import { STATUS_STYLES, fmtTime } from "@/lib/helpers";
import { toast } from "sonner";
import { ChevronUp, ChevronDown, ChevronLeft, ChevronRight, Plus, Check, GripVertical, CalendarOff, Trash2 } from "lucide-react";

const DAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const isSameDay = (a, b) => a.toDateString() === b.toDateString();
const pad2 = (n) => String(n).padStart(2, "0");
const localKey = (date) => `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}`;
const nextDayKey = (key) => { const [y, m, d] = key.split("-").map(Number); const dt = new Date(Date.UTC(y, m - 1, d)); dt.setUTCDate(dt.getUTCDate() + 1); return dt.toISOString().slice(0, 10); };

function MonthGrid({ monthDate, jobsFor, availFor, selectedDay, onSelectDay, today, onDragStartJob, onDropDay, dragActive }) {
  const year = monthDate.getFullYear();
  const month = monthDate.getMonth();
  const startDay = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const [overKey, setOverKey] = useState(null);

  const cells = [];
  for (let i = 0; i < startDay; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(new Date(year, month, d));

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4 sm:p-5" data-testid="calendar-grid">
      <h3 className="font-heading font-semibold text-slate-900 mb-3">
        {monthDate.toLocaleDateString("en-AU", { month: "long", year: "numeric" })}
      </h3>
      <div className="grid grid-cols-7 mb-2">
        {DAYS.map((d) => <div key={d} className="text-center text-xs font-bold uppercase tracking-wider text-slate-400 py-2">{d}</div>)}
      </div>
      <div className="grid grid-cols-7 gap-1">
        {cells.map((date, i) => {
          if (!date) return <div key={`e${i}`} />;
          const dj = jobsFor(date);
          const selected = isSameDay(date, selectedDay);
          const isToday = isSameDay(date, today);
          const key = date.toISOString();
          const isOver = overKey === key;
          return (
            <button
              key={key}
              onClick={() => onSelectDay(date)}
              onDragOver={(e) => { if (dragActive) { e.preventDefault(); setOverKey(key); } }}
              onDragLeave={() => setOverKey((k) => (k === key ? null : k))}
              onDrop={(e) => { e.preventDefault(); setOverKey(null); onDropDay(date); }}
              data-testid={`day-${year}-${month + 1}-${date.getDate()}`}
              className={`relative min-h-[74px] sm:min-h-[92px] rounded-lg p-1.5 text-left border transition-all ${
                isOver ? "border-lime-500 bg-lime-100 ring-2 ring-lime-300"
                : selected ? "border-lime-400 bg-lime-50" : "border-transparent hover:border-slate-200 hover:bg-slate-50"
              }`}
            >
              <span className={`text-sm font-medium inline-flex items-center justify-center h-6 w-6 rounded-full ${isToday ? "bg-lime-400 text-slate-900" : "text-slate-700"}`}>
                {date.getDate()}
              </span>
              {(() => { const av = availFor && availFor(date); const dnc = av?.dnc?.length || 0; const ok = av?.contactable?.length || 0; return (dnc > 0 || ok > 0) ? (
                <span className="absolute top-1.5 right-1.5 flex gap-0.5">
                  {dnc > 0 && <span className="h-4 min-w-4 px-1 rounded-full bg-red-500 text-white text-[9px] font-bold flex items-center justify-center" title="Unavailable — do not contact">{dnc}</span>}
                  {ok > 0 && <span className="h-4 min-w-4 px-1 rounded-full bg-amber-500 text-white text-[9px] font-bold flex items-center justify-center" title="Unavailable — OK to contact">{ok}</span>}
                </span>
              ) : null; })()}
              <div className="mt-1 space-y-1">
                {dj.slice(0, 2).map((j) => (
                  <div
                    key={j.id}
                    draggable
                    onDragStart={(e) => { e.stopPropagation(); e.dataTransfer.effectAllowed = "move"; e.dataTransfer.setData("text/plain", j.id); onDragStartJob(j.id); }}
                    data-testid={`cal-chip-${j.id}`}
                    className="text-[10px] leading-tight truncate px-1 py-0.5 rounded flex items-center gap-0.5 cursor-grab active:cursor-grabbing"
                    style={{ background: (STATUS_STYLES[j.status]?.dot || "#94a3b8") + "22", color: "#334155" }}
                  >
                    <span className="inline-block h-1.5 w-1.5 rounded-full shrink-0" style={{ background: STATUS_STYLES[j.status]?.dot }} />
                    {j.avcrm_loaded && <Check className="h-2.5 w-2.5 text-lime-600 shrink-0" strokeWidth={3} />}
                    <span className="truncate">{fmtTime(j.start)} {j.title}</span>
                  </div>
                ))}
                {dj.length > 2 && <div className="text-[10px] text-slate-400 px-1">+{dj.length - 2} more</div>}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

export default function CalendarPage() {
  const navigate = useNavigate();
  const [jobs, setJobs] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [cursor, setCursor] = useState(new Date());
  const [filter, setFilter] = useState("all");
  const [selectedDay, setSelectedDay] = useState(new Date());
  const [dialogOpen, setDialogOpen] = useState(false);
  const [activeJob, setActiveJob] = useState(null);
  const [dragJobId, setDragJobId] = useState(null);
  const [view, setView] = useState("months"); // months | year
  const [yearMode, setYearMode] = useState("calendar"); // calendar | financial
  const [yearOffset, setYearOffset] = useState(0); // shift the 12-month window by whole years
  const [availability, setAvailability] = useState([]);
  const [avOpen, setAvOpen] = useState(false);
  const [avForm, setAvForm] = useState({ employee_id: "", type: "contactable", start: "", end: "", reason: "" });

  const load = useCallback(async () => {
    const [j, e, av] = await Promise.all([api.get("/jobs"), api.get("/employees"), api.get("/availability")]);
    setJobs(j.data); setEmployees(e.data); setAvailability(av.data);
  }, []);

  useEffect(() => {
    load();
    const h = () => load();
    window.addEventListener("jobs:refresh", h);
    return () => window.removeEventListener("jobs:refresh", h);
  }, [load]);

  const empMap = Object.fromEntries(employees.map((e) => [e.id, e]));
  const filtered = useMemo(
    () => (filter === "all" ? jobs : jobs.filter((j) => j.employee_id === filter)),
    [jobs, filter]
  );

  const availFiltered = useMemo(
    () => (filter === "all" ? availability : availability.filter((a) => a.employee_id === filter)),
    [availability, filter]
  );

  const availByKey = useMemo(() => {
    const map = {};
    availFiltered.forEach((a) => {
      const name = empMap[a.employee_id]?.name || "Crew";
      const bucket = a.type === "dnc" ? "dnc" : "contactable";
      let d = a.start; const end = a.end || a.start; let g = 0;
      while (d <= end && g < 400) { (map[d] = map[d] || { dnc: [], contactable: [] }); if (!map[d][bucket].includes(name)) map[d][bucket].push(name); d = nextDayKey(d); g += 1; }
    });
    return map;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [availFiltered, employees]);
  const availFor = (date) => availByKey[localKey(date)];

  const saveAvailability = async () => {
    if (!avForm.employee_id || !avForm.start) { toast.error("Pick a crew member and a start date"); return; }
    try {
      await api.post("/availability", { ...avForm, end: avForm.end || avForm.start });
      toast.success("Unavailability added");
      setAvOpen(false);
      setAvForm({ employee_id: "", type: "contactable", start: "", end: "", reason: "" });
      load();
    } catch { toast.error("Could not save unavailability"); }
  };
  const deleteAvailability = async (id) => {
    try { await api.delete(`/availability/${id}`); load(); } catch { toast.error("Delete failed"); }
  };

  const jobsFor = (date) =>
    filtered.filter((j) => new Date(j.start).toDateString() === date.toDateString())
      .sort((a, b) => new Date(a.start) - new Date(b.start));

  const dayJobs = jobsFor(selectedDay);
  const dayAvailEntries = availFiltered.filter((a) => { const k = localKey(selectedDay); return a.start <= k && (a.end || a.start) >= k; });
  const today = new Date();

  const firstMonth = new Date(cursor.getFullYear(), cursor.getMonth(), 1);
  const secondMonth = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1);
  const goPrev = () => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() - 1, 1));
  const goNext = () => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1));

  const openJob = (job) => navigate(`/jobs/${job.id}`);
  const jumpToDay = (key) => {
    const parts = key.split("-").map(Number);
    setCursor(new Date(parts[0], parts[1] - 1, 1));
    setSelectedDay(new Date(parts[0], parts[1] - 1, parts[2]));
    setView("months");
  };
  const openNew = () => {
    const d = new Date(selectedDay); d.setHours(9, 0, 0, 0);
    setActiveJob({ start: d.toISOString() }); setDialogOpen(true);
  };

  const reschedule = async (date) => {
    const jobId = dragJobId; setDragJobId(null);
    const job = jobs.find((j) => j.id === jobId);
    if (!job) return;
    const os = new Date(job.start);
    if (isSameDay(os, date)) return;
    const ns = new Date(date); ns.setHours(os.getHours(), os.getMinutes(), 0, 0);
    let ne = null;
    if (job.end) { const dur = new Date(job.end) - os; ne = new Date(ns.getTime() + dur).toISOString(); }
    const { id, created_at, created_by, ...rest } = job;
    try {
      await api.put(`/jobs/${jobId}`, { ...rest, start: ns.toISOString(), end: ne });
      toast.success(`Moved "${job.title}" to ${ns.toLocaleDateString("en-AU", { weekday: "short", day: "numeric", month: "short" })}`);
      setSelectedDay(new Date(date));
      load();
    } catch { toast.error("Reschedule failed"); }
  };

  return (
    <div className="space-y-6 fade-up">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-lime-600 mb-1">Shared Calendar</p>
          <h1 className="text-3xl sm:text-4xl font-bold tracking-tight text-slate-900 font-heading">
            {firstMonth.toLocaleDateString("en-AU", { month: "long", year: "numeric" })}
          </h1>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="inline-flex rounded-lg border border-slate-200 bg-white p-0.5" data-testid="calendar-view-toggle">
            <button data-testid="view-months-btn" onClick={() => setView("months")} className={`px-3 py-1.5 text-sm rounded-md transition-colors ${view === "months" ? "bg-slate-900 text-white" : "text-slate-500 hover:text-slate-900"}`}>Calendar</button>
            <button data-testid="view-year-btn" onClick={() => setView("year")} className={`px-3 py-1.5 text-sm rounded-md transition-colors ${view === "year" ? "bg-slate-900 text-white" : "text-slate-500 hover:text-slate-900"}`}>12-Month</button>
          </div>
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="w-[170px]" data-testid="employee-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Pilots</SelectItem>
              {employees.map((e) => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button data-testid="today-btn" variant="outline" onClick={() => { setCursor(new Date()); setSelectedDay(new Date()); }}>Today</Button>
          <Button data-testid="add-unavailable-btn" variant="outline" onClick={() => setAvOpen(true)} className="border-amber-300 text-amber-700 hover:bg-amber-50">
            <CalendarOff className="h-4 w-4 mr-1.5" /> Unavailable
          </Button>
        </div>
      </div>

      {view === "year" && (() => {
        const now = new Date();
        const calYear = now.getFullYear() + yearOffset;
        const fyStartYear = (now.getMonth() >= 6 ? now.getFullYear() : now.getFullYear() - 1) + yearOffset;
        const yStart = yearMode === "financial" ? { y: fyStartYear, m: 6 } : { y: calYear, m: 0 };
        const rangeLabel = yearMode === "financial" ? `FY ${fyStartYear}–${String(fyStartYear + 1).slice(2)}` : `${calYear}`;
        return (
        <div data-testid="year-view">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
            <p className="text-sm text-slate-500">12-month job forecast — numbers show jobs booked each day; orange/red dots show crew unavailability. Click a day to jump to it.</p>
            <div className="flex items-center gap-2">
              <div className="inline-flex rounded-lg border border-slate-200 bg-white p-0.5" data-testid="year-mode-toggle">
                <button data-testid="year-mode-calendar" onClick={() => setYearMode("calendar")} className={`px-3 py-1.5 text-sm rounded-md transition-colors ${yearMode === "calendar" ? "bg-slate-900 text-white" : "text-slate-500 hover:text-slate-900"}`}>Calendar year</button>
                <button data-testid="year-mode-financial" onClick={() => setYearMode("financial")} className={`px-3 py-1.5 text-sm rounded-md transition-colors ${yearMode === "financial" ? "bg-slate-900 text-white" : "text-slate-500 hover:text-slate-900"}`}>Financial year</button>
              </div>
              <div className="inline-flex items-center gap-1 rounded-lg border border-slate-200 bg-white px-1 py-0.5">
                <button data-testid="year-prev" onClick={() => setYearOffset((o) => o - 1)} className="h-8 w-8 rounded-md flex items-center justify-center text-slate-500 hover:bg-slate-100"><ChevronLeft className="h-5 w-5" /></button>
                <span className="text-sm font-semibold text-slate-800 min-w-[92px] text-center font-heading" data-testid="year-range-label">{rangeLabel}</span>
                <button data-testid="year-next" onClick={() => setYearOffset((o) => o + 1)} className="h-8 w-8 rounded-md flex items-center justify-center text-slate-500 hover:bg-slate-100"><ChevronRight className="h-5 w-5" /></button>
              </div>
              {yearOffset !== 0 && (
                <Button data-testid="year-reset" size="sm" variant="outline" onClick={() => setYearOffset(0)}>This year</Button>
              )}
            </div>
          </div>
          <JobsCalendar
            jobs={filtered}
            todayKey={new Date().toLocaleDateString("en-CA", { timeZone: "Australia/Brisbane" })}
            count={12}
            startYear={yStart.y}
            startMonth={yStart.m}
            onPickDay={jumpToDay}
            empMap={empMap}
            availability={availability}
            staffFilter
            filterTop
          />
        </div>
        );
      })()}

      {view === "months" && (
      <div className="grid lg:grid-cols-3 gap-6 items-start">
        {/* Two stacked months + scroll arrows */}
        <div className="lg:col-span-2 space-y-3">
          <button
            data-testid="scroll-up-btn"
            onClick={goPrev}
            aria-label="Previous month"
            className="w-full flex items-center justify-center gap-2 py-3 rounded-xl bg-slate-900 text-white hover:bg-slate-800 transition-all hover:-translate-y-0.5"
          >
            <ChevronUp className="h-7 w-7" strokeWidth={2.5} />
            <span className="font-heading font-medium">Previous month</span>
          </button>

          <MonthGrid monthDate={firstMonth} jobsFor={jobsFor} availFor={availFor} selectedDay={selectedDay} onSelectDay={setSelectedDay} today={today} onDragStartJob={setDragJobId} onDropDay={reschedule} dragActive={!!dragJobId} />
          <MonthGrid monthDate={secondMonth} jobsFor={jobsFor} availFor={availFor} selectedDay={selectedDay} onSelectDay={setSelectedDay} today={today} onDragStartJob={setDragJobId} onDropDay={reschedule} dragActive={!!dragJobId} />
          <p className="text-xs text-slate-400 text-center flex items-center justify-center gap-1.5" data-testid="dnd-hint"><GripVertical className="h-3.5 w-3.5" /> Tip: drag a job to another day to reschedule (e.g. push a job for rain).</p>

          <button
            data-testid="scroll-down-btn"
            onClick={goNext}
            aria-label="Next month"
            className="w-full flex items-center justify-center gap-2 py-3 rounded-xl bg-lime-400 text-slate-900 hover:bg-lime-300 transition-all hover:-translate-y-0.5"
          >
            <span className="font-heading font-medium">Next month</span>
            <ChevronDown className="h-7 w-7" strokeWidth={2.5} />
          </button>
        </div>

        {/* Day detail */}
        <div className="space-y-4 lg:sticky lg:top-24">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold font-heading text-slate-900">
              {selectedDay.toLocaleDateString("en-AU", { weekday: "long", day: "numeric", month: "long" })}
            </h2>
            <Button size="sm" data-testid="add-day-job-btn" onClick={openNew} className="bg-lime-400 hover:bg-lime-300 text-slate-900">
              <Plus className="h-4 w-4" />
            </Button>
          </div>
          {dayJobs.length === 0 ? (
            <div className="bg-white border border-dashed border-slate-300 rounded-xl p-8 text-center text-sm text-slate-500">
              No jobs scheduled this day.
            </div>
          ) : (
            <div className="space-y-3" data-testid="day-jobs-list">
              {dayJobs.map((job) => (
                <div key={job.id} draggable onDragStart={(e) => { e.dataTransfer.effectAllowed = "move"; e.dataTransfer.setData("text/plain", job.id); setDragJobId(job.id); }} data-testid={`day-job-draggable-${job.id}`}>
                  <JobCard job={job} employee={empMap[job.employee_id]} empMap={empMap} onClick={() => openJob(job)} />
                  <JobWeather location={job.location} dateISO={job.start} />
                </div>
              ))}
            </div>
          )}

          {dayAvailEntries.length > 0 && (
            <div className="space-y-2" data-testid="day-unavailable-list">
              <h3 className="text-xs font-bold uppercase tracking-wider text-slate-400 flex items-center gap-1.5"><CalendarOff className="h-3.5 w-3.5" /> Crew unavailable</h3>
              {dayAvailEntries.map((a) => (
                <div key={a.id} data-testid={`day-unavailable-${a.id}`}
                  className={`flex items-start gap-2 rounded-lg border px-3 py-2 ${a.type === "dnc" ? "border-red-200 bg-red-50" : "border-amber-200 bg-amber-50"}`}>
                  <span className={`h-2.5 w-2.5 rounded-full mt-1.5 shrink-0 ${a.type === "dnc" ? "bg-red-500" : "bg-amber-500"}`} />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-slate-800">{empMap[a.employee_id]?.name || "Crew"}</p>
                    <p className={`text-xs ${a.type === "dnc" ? "text-red-700" : "text-amber-700"}`}>{a.type === "dnc" ? "Do not contact" : "OK to contact"}{a.reason ? ` · ${a.reason}` : ""}</p>
                  </div>
                  <button onClick={() => deleteAvailability(a.id)} data-testid={`day-unavailable-delete-${a.id}`} className="h-6 w-6 rounded flex items-center justify-center text-slate-400 hover:bg-white hover:text-red-600"><Trash2 className="h-3.5 w-3.5" /></button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
      )}

      <JobDialog open={dialogOpen} onOpenChange={setDialogOpen} job={activeJob} />

      <Dialog open={avOpen} onOpenChange={setAvOpen}>
        <DialogContent data-testid="unavailable-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 font-heading"><CalendarOff className="h-5 w-5 text-amber-500" /> Add Unavailability</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-xs font-semibold text-slate-600">Crew member</Label>
              <Select value={avForm.employee_id} onValueChange={(v) => setAvForm((f) => ({ ...f, employee_id: v }))}>
                <SelectTrigger className="mt-1" data-testid="av-employee"><SelectValue placeholder="Select crew…" /></SelectTrigger>
                <SelectContent>
                  {employees.map((e) => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs font-semibold text-slate-600">Type</Label>
              <div className="grid grid-cols-2 gap-2 mt-1">
                <button type="button" data-testid="av-type-contactable" onClick={() => setAvForm((f) => ({ ...f, type: "contactable" }))}
                  className={`flex items-center gap-2 rounded-lg border px-3 py-2 text-sm text-left transition-all ${avForm.type === "contactable" ? "border-amber-400 bg-amber-50 text-amber-800" : "border-slate-200 text-slate-600 hover:border-slate-300"}`}>
                  <span className="h-3 w-3 rounded-full bg-amber-500 shrink-0" /> Unavailable — OK to contact
                </button>
                <button type="button" data-testid="av-type-dnc" onClick={() => setAvForm((f) => ({ ...f, type: "dnc" }))}
                  className={`flex items-center gap-2 rounded-lg border px-3 py-2 text-sm text-left transition-all ${avForm.type === "dnc" ? "border-red-400 bg-red-50 text-red-800" : "border-slate-200 text-slate-600 hover:border-slate-300"}`}>
                  <span className="h-3 w-3 rounded-full bg-red-500 shrink-0" /> Unavailable — do not contact
                </button>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs font-semibold text-slate-600">From</Label>
                <Input type="date" className="mt-1" data-testid="av-start" value={avForm.start} onChange={(e) => setAvForm((f) => ({ ...f, start: e.target.value }))} />
              </div>
              <div>
                <Label className="text-xs font-semibold text-slate-600">To (optional)</Label>
                <Input type="date" className="mt-1" data-testid="av-end" value={avForm.end} min={avForm.start} onChange={(e) => setAvForm((f) => ({ ...f, end: e.target.value }))} />
              </div>
            </div>
            <div>
              <Label className="text-xs font-semibold text-slate-600">Reason (optional)</Label>
              <Textarea className="mt-1 resize-none" rows={2} data-testid="av-reason" value={avForm.reason} onChange={(e) => setAvForm((f) => ({ ...f, reason: e.target.value }))} placeholder="e.g. Annual leave, medical, training…" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAvOpen(false)} data-testid="av-cancel">Cancel</Button>
            <Button onClick={saveAvailability} data-testid="av-save" className="bg-slate-900 hover:bg-slate-800 text-white">Add</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
